import React, { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { api, i18n } from "@/lib/api";
import { bootstrapSupabase, getBootstrapDiagnostics, getBootstrapError, getSupabase, isSupabaseConfigured } from "@/lib/supabaseClient";

import AdminPortal from "@/components/AdminPortal";
import AuthScreen from "@/components/AuthScreen";
import DisclosurePanel from "@/components/DisclosurePanel";
import Generator from "@/components/Generator";
import { GlassesIcon } from "@/components/GlassesIcon";
import MorningBriefing from "@/components/MorningBriefing";
import OfflineBanner from "@/components/OfflineBanner";
import ProfileHub from "@/components/ProfileHub";
import Splash from "@/components/Splash";
import SyllabusPanel from "@/components/SyllabusPanel";
import VaultLibrary from "@/components/VaultLibrary";
import WelcomeScreen from "@/components/WelcomeScreen";

const TAB_BRIEFING = "briefing";
const TAB_WORKSPACE = "workspace";
const TAB_PROFILE = "profile";
const TAB_SYLLABUS = "syllabus";
const TAB_VAULT = "vault";
const TAB_ADMIN = "admin";

function detectLangFromUrlOrStorage() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("lang");
  if (fromUrl === "es" || fromUrl === "en") return fromUrl;
  const saved = localStorage.getItem("pta_lang");
  if (saved === "es" || saved === "en") return saved;
  return "en";
}

function captureReferralFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && /^[0-9a-fA-F-]{20,}$/.test(ref)) {
      const existing = localStorage.getItem("pta_referrer");
      if (!existing) {
        localStorage.setItem("pta_referrer", ref);
      }
    }
  } catch (e) {
    /* noop */
  }
}

function captureCheckoutReturnFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const checkout = params.get("checkout");
    const topup = params.get("topup");
    if (sessionId && (checkout === "success" || topup === "success")) {
      const payload = {
        session_id: sessionId,
        kind: topup === "success" ? "topup" : "subscription",
      };
      localStorage.setItem("pta_checkout_return", JSON.stringify(payload));
    }
    // Always clean the URL so query params don't leak through navigation
    if (params.has("checkout") || params.has("topup") || params.has("session_id")) {
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, clean);
    }
  } catch (e) {
    /* noop */
  }
}

captureReferralFromUrl();
captureCheckoutReturnFromUrl();

export default function App() {
  const [language, setLanguage] = useState(detectLangFromUrlOrStorage());
  const [splashDone, setSplashDone] = useState(false);
  const [bootstrapState, setBootstrapState] = useState({ done: false, configured: false, error: null, diagnostics: null });
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [syllabi, setSyllabi] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tab, setTab] = useState(TAB_BRIEFING);
  const [bootError, setBootError] = useState("");
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [authInitialTab, setAuthInitialTab] = useState(null);
  const [postCheckout, setPostCheckout] = useState({ pending: false, kind: null, status: "idle", message: "" });

  const t = i18n[language];

  useEffect(() => {
    localStorage.setItem("pta_lang", language);
  }, [language]);

  // Async bootstrap of Supabase from backend /api/public/config
  useEffect(() => {
    bootstrapSupabase().then((state) => {
      setBootstrapState({
        done: true,
        configured: !!state.configured,
        error: state.error || null,
        diagnostics: state.diagnostics || null,
      });
    });
  }, []);

  // Subscribe to auth state changes once supabase is configured
  useEffect(() => {
    if (!bootstrapState.done || !bootstrapState.configured) return undefined;
    const client = getSupabase();
    if (!client) return undefined;
    client.auth.getSession().then(({ data }) => setSession(data?.session || null));
    const { data: sub } = client.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub?.subscription?.unsubscribe?.();
  }, [bootstrapState.done, bootstrapState.configured]);

  const refreshProfile = useCallback(async () => {
    try {
      const r = await api.getProfile();
      setProfile(r.profile);
      if (r.profile?.preferred_language && r.profile.preferred_language !== language) {
        setLanguage(r.profile.preferred_language);
      }
      if (!r.profile?.disclosure_accepted_at) {
        setShowDisclosure(true);
      }
      try {
        const pendingRef = localStorage.getItem("pta_referrer");
        if (pendingRef && r.profile?.id && pendingRef !== r.profile.id && !r.profile.referred_by_user_id) {
          await api.bindReferral(pendingRef);
        }
        if (pendingRef) localStorage.removeItem("pta_referrer");
      } catch (e) {
        /* noop */
      }
    } catch (e) {
      setBootError(e?.response?.data?.detail || e?.message || "Profile load failed");
    }
  }, [language]);

  const refreshSyllabi = useCallback(async () => {
    try {
      const r = await api.listSyllabi();
      setSyllabi(r.items || []);
    } catch (e) {
      /* noop */
    }
  }, []);

  const refreshDocuments = useCallback(async () => {
    try {
      const r = await api.listDocuments();
      setDocuments(r.items || []);
    } catch (e) {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    refreshProfile();
    refreshSyllabi();
    refreshDocuments();
  }, [session, refreshProfile, refreshSyllabi, refreshDocuments]);

  // Real-time credit counter: subscribe to this user's profile row so credit
  // balances tick down instantly when a generation finishes - no page refresh.
  useEffect(() => {
    const client = getSupabase();
    const uid = profile?.id;
    if (!client || !uid) return undefined;
    const channel = client
      .channel(`profile-credits-${uid}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "user_profiles", filter: `id=eq.${uid}` },
        (payload) => {
          const row = payload?.new;
          if (row) setProfile((prev) => (prev ? { ...prev, ...row } : row));
        },
      )
      .subscribe();
    return () => {
      client.removeChannel(channel);
    };
  }, [profile?.id]);

  // After Stripe redirect lands the user back, defensively verify the session
  // and activate the subscription / top-up via the backend endpoint. This
  // works even when the Stripe webhook isn't configured or hasn't fired.
  useEffect(() => {
    if (!session) return;
    let raw;
    try {
      raw = localStorage.getItem("pta_checkout_return");
    } catch (e) {
      return;
    }
    if (!raw) return;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      localStorage.removeItem("pta_checkout_return");
      return;
    }
    if (!parsed?.session_id) {
      localStorage.removeItem("pta_checkout_return");
      return;
    }
    setPostCheckout({ pending: true, kind: parsed.kind, status: "verifying", message: "" });
    (async () => {
      try {
        const result = await api.stripeVerifySession(parsed.session_id);
        if (result?.profile) setProfile(result.profile);
        await refreshProfile();
        await refreshDocuments();
        setPostCheckout({
          pending: true,
          kind: parsed.kind,
          status: result?.ok ? "success" : "warning",
          message: result?.reason || "",
        });
        setTab(TAB_WORKSPACE);
        setTimeout(() => setPostCheckout({ pending: false, kind: null, status: "idle", message: "" }), 5000);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Verification failed";
        setPostCheckout({ pending: true, kind: parsed.kind, status: "error", message: msg });
        setTimeout(() => setPostCheckout({ pending: false, kind: null, status: "idle", message: "" }), 8000);
      } finally {
        try {
          localStorage.removeItem("pta_checkout_return");
        } catch (e) {
          /* noop */
        }
      }
    })();
  }, [session, refreshProfile, refreshDocuments]);

  const acceptDisclosure = async () => {
    try {
      const r = await api.updateProfile({ disclosure_accepted: true, preferred_language: language });
      setProfile(r.profile);
      setShowDisclosure(false);
    } catch (e) {
      setShowDisclosure(false);
    }
  };

  const changeLanguage = async (lang) => {
    setLanguage(lang);
    if (session) {
      try {
        const r = await api.updateProfile({ preferred_language: lang });
        setProfile(r.profile);
      } catch (e) {
        /* noop */
      }
    }
  };

  const handleLogout = async () => {
    const client = getSupabase();
    await client?.auth.signOut();
    setSession(null);
    setProfile(null);
    setDocuments([]);
    setSyllabi([]);
    setTab(TAB_BRIEFING);
  };

  const startSubscription = async () => {
    setStripeBusy(true);
    try {
      const r = await api.stripeSubscription(window.location.origin);
      if (r.url) window.location.href = r.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Stripe checkout unavailable");
    } finally {
      setStripeBusy(false);
    }
  };

  const startTopup = async () => {
    setStripeBusy(true);
    try {
      const r = await api.stripeTopup(window.location.origin);
      if (r.url) window.location.href = r.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Stripe checkout unavailable");
    } finally {
      setStripeBusy(false);
    }
  };

  const openPortal = async () => {
    setStripeBusy(true);
    try {
      const r = await api.stripePortal(window.location.origin);
      if (r.url) window.location.href = r.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Customer portal unavailable");
    } finally {
      setStripeBusy(false);
    }
  };

  // Splash gate
  if (!splashDone) {
    return <Splash onDone={() => setSplashDone(true)} />;
  }

  // Bootstrap still loading
  if (!bootstrapState.done) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} data-testid="bootstrap-loading">
        <div style={{ textAlign: "center" }}>
          <div className="h-eyebrow">POCKET TA AI</div>
          <div className="txt-mute live-dot" style={{ marginTop: "0.5rem" }}>
            {t.connecting || "Connecting"}
          </div>
        </div>
      </div>
    );
  }

  // Auth gate (uses bootstrap state)
  if (!session) {
    // If Supabase is properly configured AND the user hasn't chosen an auth path
    // yet, show the Welcome / Pricing landing first.
    if (bootstrapState.configured && !authInitialTab) {
      return (
        <>
          <OfflineBanner message={t.offlineMessage} />
          <WelcomeScreen
            language={language}
            onLanguageChange={changeLanguage}
            onChoose={(choice) => setAuthInitialTab(choice)}
          />
        </>
      );
    }
    return (
      <>
        <OfflineBanner message={t.offlineMessage} />
        <AuthScreen
          language={language}
          onLanguageChange={changeLanguage}
          onAuthenticated={() => { /* session listener handles */ }}
          configured={bootstrapState.configured}
          bootstrapError={bootstrapState.error}
          bootstrapDiagnostics={bootstrapState.diagnostics}
          initialTab={authInitialTab || "login"}
          onBack={() => setAuthInitialTab(null)}
        />
      </>
    );
  }

  if (showDisclosure) {
    return (
      <>
        <OfflineBanner message={t.offlineMessage} />
        <DisclosurePanel language={language} onAccept={acceptDisclosure} />
      </>
    );
  }

  const subStatus = profile?.subscription_status || "inactive";
  const locked = !(subStatus === "active" || subStatus === "trialing");
  const isAdmin = profile?.is_admin === true;
  const baseCreds = profile?.base_credits_remaining ?? 0;
  const topupCreds = profile?.purchased_credits_remaining ?? 0;

  return (
    <>
      <OfflineBanner message={t.offlineMessage} />
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "1.5rem 1.5rem 4rem" }}>
        {postCheckout.pending && (
          <div
            data-testid="post-checkout-banner"
            style={{
              padding: "0.85rem 1rem",
              marginBottom: "1rem",
              borderTop: "1px solid var(--accent)",
              borderBottom: "1px solid var(--accent)",
              color:
                postCheckout.status === "error"
                  ? "var(--danger)"
                  : postCheckout.status === "warning"
                  ? "var(--ink-mute)"
                  : "var(--ink)",
              fontSize: "0.92rem",
              letterSpacing: "0.01em",
            }}
          >
            {postCheckout.status === "verifying" && (
              <span className="live-dot">
                {language === "es"
                  ? "Activando tu pago"
                  : "Activating your payment"}
              </span>
            )}
            {postCheckout.status === "success" && (
              <span>
                {postCheckout.kind === "topup"
                  ? language === "es"
                    ? "Recarga exitosa. 300 creditos anadidos a tu cuenta."
                    : "Top-up successful. 300 credits added to your account."
                  : language === "es"
                  ? "Suscripcion activa. 500 creditos cargados. Bienvenido a POCKET TA AI."
                  : "Subscription active. 500 credits loaded. Welcome to POCKET TA AI."}
              </span>
            )}
            {postCheckout.status === "warning" && (
              <span>
                {language === "es" ? "Verificacion pendiente: " : "Verification pending: "}
                {postCheckout.message}
              </span>
            )}
            {postCheckout.status === "error" && (
              <span>
                {language === "es" ? "Error verificando el pago: " : "Payment verification error: "}
                {postCheckout.message}
              </span>
            )}
          </div>
        )}
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }} data-testid="app-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
            <GlassesIcon size={42} />
            <div>
              <div className="h-eyebrow">GO OUTDOORS</div>
              <div className="h-display" style={{ fontSize: "1.25rem", marginTop: "0.1rem" }}>POCKET TA AI</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
            <span className="kbd" data-testid="credit-summary">
              {t.baseCredits} {baseCreds} · {t.topupCredits} {topupCreds}
            </span>
            <div className="lang-toggle" data-testid="header-lang-toggle">
              <button data-testid="header-lang-en" className={language === "en" ? "active" : ""} onClick={() => changeLanguage("en")}>EN</button>
              <button data-testid="header-lang-es" className={language === "es" ? "active" : ""} onClick={() => changeLanguage("es")}>ES</button>
            </div>
            <button data-testid="logout-btn" className="btn-link" onClick={handleLogout}>{t.logout}</button>
          </div>
        </header>

        <nav style={{ display: "flex", gap: "1.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }} data-testid="primary-nav">
          {[
            { key: TAB_BRIEFING, label: t.briefing },
            { key: TAB_WORKSPACE, label: t.workspace },
            { key: TAB_VAULT, label: t.vault },
            { key: TAB_PROFILE, label: t.profile },
            { key: TAB_SYLLABUS, label: t.syllabus },
            ...(isAdmin ? [{ key: TAB_ADMIN, label: t.admin }] : []),
          ].map((nav) => (
            <button
              key={nav.key}
              data-testid={`nav-${nav.key}`}
              className="btn-link"
              onClick={() => setTab(nav.key)}
              style={{
                color: tab === nav.key ? "var(--ink)" : "var(--ink-mute)",
                borderBottomColor: tab === nav.key ? "var(--ink)" : "transparent",
                fontSize: "0.95rem",
              }}
            >
              {nav.label}
            </button>
          ))}
        </nav>

        <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap" }} data-testid="billing-strip">
          {locked ? (
            <button data-testid="subscribe-btn" className="btn-primary" disabled={stripeBusy} onClick={startSubscription}>
              {t.subscribe}
            </button>
          ) : (
            <span className="txt-mute" style={{ fontSize: "0.92rem" }} data-testid="sub-active-label">
              {language === "es" ? "Suscripcion activa" : "Subscription active"}
            </span>
          )}
          <button data-testid="topup-btn" className="btn-ghost" disabled={stripeBusy} onClick={startTopup}>
            {t.addTopup}
          </button>
          {profile?.stripe_customer_id && (
            <button data-testid="portal-btn" className="btn-link" disabled={stripeBusy} onClick={openPortal}>
              {t.manageBilling}
            </button>
          )}
        </div>

        <div className="line-strong" />

        {bootError && <p className="txt-danger" data-testid="boot-error">{bootError}</p>}

        {tab === TAB_BRIEFING && <MorningBriefing language={language} />}
        {tab === TAB_WORKSPACE && (
          <Generator
            language={language}
            profile={profile}
            syllabi={syllabi}
            locked={locked}
            onGenerated={() => refreshDocuments()}
          />
        )}
        {tab === TAB_VAULT && (
          <VaultLibrary language={language} items={documents} currentUserId={profile?.id} onRefresh={refreshDocuments} />
        )}
        {tab === TAB_PROFILE && (
          <ProfileHub language={language} profile={profile} onSaved={(p) => setProfile(p)} />
        )}
        {tab === TAB_SYLLABUS && (
          <SyllabusPanel language={language} items={syllabi} onChanged={refreshSyllabi} />
        )}
        {tab === TAB_ADMIN && isAdmin && <AdminPortal language={language} />}

        <div className="line-strong" />
        <footer className="kbd" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem", color: "var(--ink-faint)" }}>
          <span>POCKET TA AI / GO OUTDOORS</span>
          <span>{t.tagline}</span>
        </footer>
      </div>
    </>
  );
}
